import { getPlanFieldConfig, FIELD_TYPES } from './planFieldConfigs';

/**
 * Normalizes a datetime-local value (e.g., "2026-04-03T18:01") to ISO 8601
 * with seconds and UTC timezone suffix (e.g., "2026-04-03T18:01:00Z").
 * @param {string} value - datetime-local input value
 * @returns {string} ISO 8601 formatted string
 */
const normalizeDateTime = (value) => {
  if (!value) return value;
  // Already has timezone info — leave as-is
  if (value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)) return value;
  // Add seconds if missing (datetime-local gives "YYYY-MM-DDTHH:MM")
  const parts = value.split('T');
  if (parts.length === 2 && parts[1].split(':').length === 2) {
    return value + ':00Z';
  }
  return value + 'Z';
};

/**
 * Build caseSpecificData object from custom field values.
 * Handles three kinds of fields:
 * - autoValue: uses the static value defined in config (e.g., agentType)
 * - formDataKey: sources the value from the main form (e.g., externalPatientId ← patientExternalId)
 * - regular: uses the value entered in the caseSpecificData UI
 *
 * Datetime fields are automatically normalized to ISO 8601 with "Z" suffix.
 *
 * @param {string} planName - Name of the plan
 * @param {Object} customFieldValues - Custom field values from caseSpecificData UI
 * @param {Object} [orgContext={}] - Organization context
 * @param {string} [planType=null] - outcomePlanType for type-based config fallback
 * @param {Object} [formData={}] - Main form data for formDataKey resolution
 * @returns {Object} caseSpecificData object
 */
export const buildCaseSpecificData = (planName, customFieldValues, orgContext = {}, planType = null, formData = {}) => {
  const config = getPlanFieldConfig(planName, orgContext, planType);
  if (!config) return {};

  const data = {};
  config.fields.forEach((field) => {
    let value;
    if (field.autoValue !== undefined) {
      value = field.autoValue;
    } else if (field.formDataKey) {
      value = formData[field.formDataKey] ?? '';
    } else {
      value = customFieldValues[field.name] ?? '';
    }

    // Normalize datetime fields to ISO 8601 with Z suffix
    if (field.type === FIELD_TYPES.DATETIME) {
      value = normalizeDateTime(value);
    }

    data[field.name] = value;
  });
  return data;
};
